import React, { useMemo, useState } from 'react';
import { LuChevronRight, LuInbox, LuSearchX } from 'react-icons/lu';
import { sourceLabel, sourceTone } from '../../../utils/sources.js';
import { Card, CardHeader } from '../../ui/Card';
import { Table, THead, Th, SortTh, TBody, Tr, Td } from '../../ui/Table';
import { SearchInput } from '../../ui/SearchInput';
import Alert from '../../ui/Alert';
import Avatar from '../../ui/Avatar';
import Badge from '../../ui/Badge';
import IconButton from '../../ui/IconButton';
import EmptyState from '../../ui/EmptyState';
import Skeleton from '../../ui/Skeleton';
import { formatDateTime } from '../../../utils/weekFormat.js';
import { foldAccents } from '../../../utils/text.js';
import StoresFoundBadge from './StoresFoundBadge';
import { compareBy } from './postList.js';
import { shortVersion, versionTitle } from './topicsVersion.js';

const ClientsView = ({ status, clients, onOpen }) => {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'client_name', direction: 'asc' });

  const rows = useMemo(() => {
    const q = foldAccents(query.trim());
    return clients
      .filter((c) => !q || foldAccents(c.client_name).includes(q) || c.client_slug.includes(q))
      .sort(compareBy(sort.key, sort.direction));
  }, [clients, query, sort]);

  const requestSort = (key) => setSort((s) => ({ key, direction: s.key === key && s.direction === 'asc' ? 'desc' : 'asc' }));

  return (
    <Card>
      <CardHeader
        icon={LuInbox}
        title="Clients"
        badge={status === 'ready' && <Badge tone="violet" size="sm">{clients.length}</Badge>}
        subtitle="Cliquez sur un client pour voir ses posts traités"
        actions={<SearchInput value={query} onChange={setQuery} placeholder="Rechercher un client…" label="Rechercher un client par son nom" className="w-full sm:w-64" />}
      />
      {status === 'error' ? (
        <div className="p-4 sm:p-5"><Alert tone="danger">Impossible de charger les clients. Veuillez réessayer.</Alert></div>
      ) : status !== 'ready' ? (
        <div className="space-y-2 p-4 sm:p-5">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}</div>
      ) : !clients.length ? (
        <EmptyState icon={LuSearchX} tone="gray" title="Aucun post analysé" description="Lancez bin/cron_visit_topics.js sur des posts : ils apparaîtront ici, rangés par client." />
      ) : !rows.length ? (
        <EmptyState icon={LuSearchX} tone="gray" title="Aucun client" description={`Aucun client ne correspond à « ${query.trim()} ».`} />
      ) : (
        <Table>
          <THead>
            <SortTh column="client_name" sort={sort} onSort={requestSort}>Client</SortTh>
            <SortTh column="posts" sort={sort} onSort={requestSort}>Posts</SortTh>
            <SortTh column="rows" sort={sort} onSort={requestSort}>Lignes</SortTh>
            <SortTh column="proposals" sort={sort} onSort={requestSort}>Propositions</SortTh>
            <SortTh column="empty_posts" sort={sort} onSort={requestSort}>Vides</SortTh>
            <SortTh column="stores_found" sort={sort} onSort={requestSort}>Magasins</SortTh>
            <Th>Versions</Th>
            <SortTh column="analysed_at" sort={sort} onSort={requestSort}>Analysé le</SortTh>
            <Th className="text-right"><span className="sr-only">Ouvrir</span></Th>
          </THead>
          <TBody>
            {rows.map((client) => (
              <Tr key={client.client_slug} className="cursor-pointer" onClick={() => onOpen(client.client_slug)}>
                <Td className="whitespace-nowrap">
                  <span className="flex items-center gap-2.5">
                    <Avatar text={client.client_name} size="sm" />
                    <span className="min-w-0">
                      <span className="block font-medium text-gray-900 dark:text-white">{client.client_name}</span>
                      <span className="flex items-center gap-1.5">
                        <span className="font-mono text-xs text-gray-400 dark:text-gray-500">{client.client_slug}</span>
                        <Badge size="sm" tone={sourceTone(client.source)}>{sourceLabel(client.source)}</Badge>
                      </span>
                    </span>
                  </span>
                </Td>
                <Td className="tabular-nums">{client.posts}</Td>
                <Td className="tabular-nums">{client.rows}</Td>
                <Td>{client.proposals ? <Badge size="sm" tone="amber">{client.proposals}</Badge> : <span className="text-gray-300 dark:text-gray-600">—</span>}</Td>
                <Td className="tabular-nums">{client.empty_posts || <span className="text-gray-300 dark:text-gray-600">—</span>}</Td>
                <Td>
                  <StoresFoundBadge found={client.stores_found} visits={client.visits} />
                </Td>
                <Td>
                  <span className="flex flex-wrap gap-1">
                    {client.versions.map((v) => <Badge key={v} size="sm" tone="violet" title={versionTitle(v)}>{shortVersion(v)}</Badge>)}
                  </span>
                </Td>
                <Td className="whitespace-nowrap text-xs">{formatDateTime(client.analysed_at)}</Td>
                <Td className="whitespace-nowrap text-right">
                  <IconButton
                    icon={LuChevronRight}
                    label={`Voir les posts de ${client.client_name}`}
                    onClick={(e) => { e.stopPropagation(); onOpen(client.client_slug); }}
                  />
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
};

export default ClientsView;
